import { useState } from 'react';
import {
  X,
  Layout,
  BookOpen,
  Loader2,
  ChevronRight,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import {
  useListSkillTemplates,
  useInstallTemplate,
} from '../../../../api/agent-skills';
import type { SkillTemplate } from '../../../../api/agent-skills';
import { useIsAdmin } from '../../../../components/onboarding-new/api';
import { ScopePickerDialog } from './ScopePickerDialog';
import { TEMPLATE_CATEGORY_META } from '../utils/constants';

import { useTranslation } from 'react-i18next';

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  onInstalled: (skillId: string) => void;
  /** When true, renders as full panel instead of modal (onboarding mode). */
  asPanel?: boolean;
}

const ALL_CATEGORIES: SkillTemplate['category'][] = [
  'security',
  'compliance',
  'reporting',
  'devops',
  'general',
];

/* ------------------------------------------------------------------ */
/* Skeleton card for the loading state                                 */
/* ------------------------------------------------------------------ */
function TemplateCardSkeleton() {
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-4">
        <div className="mb-2 flex items-start justify-between">
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-14 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="mb-3 space-y-1.5">
          <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-7 w-16 animate-pulse rounded-md bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-14 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    </div>
  );
}

function TemplateGridSkeleton() {
  return (
    <div className="space-y-6 p-5">
      {/* Category header skeleton */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="h-6 w-6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-3 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <TemplateCardSkeleton key={`a-${i}`} />
          ))}
        </div>
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="h-6 w-6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-3 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <TemplateCardSkeleton key={`b-${i}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function TemplateGallery({
  isOpen,
  onClose,
  onInstalled,
  asPanel,
}: TemplateGalleryProps) {
  const { t } = useTranslation('pleri/skills');
  const {
    data: templates,
    isLoading,
    isError,
    refetch,
  } = useListSkillTemplates();
  const installTemplate = useInstallTemplate();
  const isAdmin = useIsAdmin();
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);
  const [scopePickerTemplate, setScopePickerTemplate] = useState<SkillTemplate | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<
    SkillTemplate['category'] | 'all'
  >('all');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const filteredTemplates =
    selectedCategory === 'all'
      ? (templates ?? [])
      : (templates ?? []).filter((t) => t.category === selectedCategory);

  const grouped = filteredTemplates.reduce<Record<string, SkillTemplate[]>>(
    (acc, t) => {
      const cat = t.category;
      if (!acc[cat]) {
        acc[cat] = [];
      }
      acc[cat].push(t);
      return acc;
    },
    {},
  );

  const doInstall = async (template: SkillTemplate, scope: 'user' | 'tenant') => {
    setScopePickerTemplate(null);
    setInstallingId(template.id);
    setInstallError(null);
    try {
      const result = await installTemplate.mutateAsync({ templateId: template.id, scope });
      onInstalled(`${result.scope}:${result.name}`);
      onClose();
    } catch (err) {
      setInstallError(
        err instanceof Error ? err.message : 'Failed to install template',
      );
    } finally {
      setInstallingId(null);
    }
  };

  const handleFork = (template: SkillTemplate) => {
    if (isAdmin) {
      setScopePickerTemplate(template);
    } else {
      doInstall(template, 'user');
    }
  };

  const content = (
    <div className="flex h-full">
      {/* Left sidebar: category filter */}
      <div className="w-48 flex-shrink-0 border-r border-gray-200 py-4 dark:border-gray-700">
        <p className="mb-2 px-4 text-xs font-semibold tracking-wider text-gray-400 uppercase">
          {t('categories')}
        </p>
        <button
          onClick={() => setSelectedCategory('all')}
          className={`flex w-full items-center gap-2 px-4 py-1.5 text-xs transition-colors duration-150 ${
            selectedCategory === 'all'
              ? 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300'
              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          {t('all')}
        </button>
        {ALL_CATEGORIES.map((cat) => {
          const meta = TEMPLATE_CATEGORY_META[cat];
          const Icon = meta.icon;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`flex w-full items-center gap-2 px-4 py-1.5 text-xs transition-colors duration-150 ${
                selectedCategory === cat
                  ? 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <div
                className={`flex h-5 w-5 items-center justify-center rounded ${meta.bg}`}
              >
                <Icon className={`h-3 w-3 ${meta.color}`} />
              </div>
              <span className="capitalize">{cat}</span>
            </button>
          );
        })}
      </div>

      {/* Template grid */}
      <div className="min-h-[300px] flex-1 overflow-y-auto">
        {/* Fork error banner */}
        {installError && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{installError}</span>
            <button
              onClick={() => setInstallError(null)}
              className="flex-shrink-0 text-red-400 hover:text-red-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {isLoading ? (
          <TemplateGridSkeleton />
        ) : isError ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
            <AlertTriangle className="mb-2 h-6 w-6 text-red-400" />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('failedToLoadTemplates')}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-2 rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-orange-700"
            >
              {t('tryAgain')}
            </button>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
            <BookOpen className="mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('noTemplatesAvailableYet')}
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {t('checkBackLaterForNewSkillTemplates')}
            </p>
          </div>
        ) : (
          <div className="space-y-6 p-5">
            {Object.entries(grouped).map(([category, catTemplates]) => {
              const meta =
                TEMPLATE_CATEGORY_META[category as SkillTemplate['category']] ??
                TEMPLATE_CATEGORY_META.general;
              const Icon = meta.icon;
              return (
                <div key={category}>
                  <div className="mb-3 flex items-center gap-2">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded ${meta.bg}`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                    </div>
                    <h3 className="text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400">
                      {category}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {catTemplates.map((template) => {
                      const isExpanded = expandedTemplate === template.id;
                      return (
                        <div
                          key={template.id}
                          className={`flex flex-col rounded-lg border border-gray-200 transition-all duration-200 hover:border-orange-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-orange-700 ${
                            isExpanded ? 'lg:col-span-2' : ''
                          }`}
                        >
                          <div className="p-4">
                            <div className="mb-2 flex items-start justify-between">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {template.displayName}
                              </h4>
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.bg} ${meta.color}`}
                              >
                                {category}
                              </span>
                            </div>
                            <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                              {template.description}
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleFork(template)}
                                disabled={installingId === template.id || !!installingId}
                                className="flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {installingId === template.id ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    {t('adding')}
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-3 w-3" />
                                    {t('common:add')}
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() =>
                                  setExpandedTemplate(
                                    isExpanded ? null : template.id,
                                  )
                                }
                                className="flex items-center gap-1 text-xs text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:hover:text-gray-300"
                              >
                                <ChevronRight
                                  className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                />
                                {isExpanded ? t('common:hide') : t('preview')}
                              </button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                              <div className="max-h-60 overflow-auto rounded-md bg-gray-50 p-3 dark:bg-gray-900">
                                <ChatMarkdown content={template.instructions} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const scopeDialog = scopePickerTemplate ? (
    <ScopePickerDialog
      templateName={scopePickerTemplate.displayName}
      onConfirm={(scope) => doInstall(scopePickerTemplate, scope)}
      onCancel={() => setScopePickerTemplate(null)}
      isLoading={installingId === scopePickerTemplate.id}
    />
  ) : null;

  // Onboarding mode: render as full panel (not modal)
  if (asPanel) {
    return (
      <div className="flex h-full min-h-[400px] flex-col">
        {scopeDialog}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Layout className="h-4 w-4 text-orange-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('getStartedWithSkillTemplates')}
            </h2>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">{content}</div>
      </div>
    );
  }

  // Normal modal mode
  return (
    <>
      {scopeDialog}
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('browseSkillTemplates')}
      >
        <div
          className="flex min-h-[400px] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-800"
          style={{ maxHeight: '80vh' }}
        >
          {/* Header */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Layout className="h-4 w-4 text-orange-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('skillTemplates')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-hidden">{content}</div>
        </div>
      </div>
    </>
  );
}
